import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { logError } from '../../../../utils/logger';
import { ModalWrapper, SectionTitle } from '../../../shared/components';
import DialogShell from '../../../tasks/components/DialogShell';
import { EmployeePay, inr, localDate, PaymentMode, Profile, Revision, setupApi } from '../../payroll.api';
import { isBadNumber, num, str } from './fields';

const fmtDate = (d: string | null) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-IN') : '—');

const bankComplete = (p: Profile) => p.paymentMode !== 'BANK' || Boolean(p.bankAccountNo && p.bankIfsc && p.bankAccountName);

type ProfileDraft = Omit<Profile, 'tdsMonthly'> & { tdsMonthly: string };

function ProfileDialog({ employee, onClose, onSaved }: { employee: EmployeePay | null; onClose: () => void; onSaved: () => void }) {
  const [d, setD] = useState<ProfileDraft | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setD(employee ? { ...employee.profile, tdsMonthly: str(employee.profile.tdsMonthly) } : null);
  }, [employee]);

  const set = (patch: Partial<ProfileDraft>) => setD((x) => (x ? { ...x, ...patch } : x));

  const save = async () => {
    if (!employee || !d) return;
    if (isBadNumber(d.tdsMonthly)) {
      toast.error('TDS must be a number.');
      return;
    }
    if (d.bankIfsc && !/^[A-Za-z]{4}0[A-Za-z0-9]{6}$/.test(d.bankIfsc.trim())) {
      toast.error('IFSC should look like HDFC0001234 (11 characters, 5th is zero).');
      return;
    }
    setBusy(true);
    try {
      const clean = (v: string | null) => (v && v.trim() ? v.trim() : null);
      await setupApi.saveProfile(employee.employeeId, {
        ...d,
        tdsMonthly: num(d.tdsMonthly),
        uanNo: clean(d.uanNo),
        esicNo: clean(d.esicNo),
        bankAccountName: clean(d.bankAccountName),
        bankName: clean(d.bankName),
        bankBranch: clean(d.bankBranch),
        bankAccountNo: clean(d.bankAccountNo),
        bankIfsc: clean(d.bankIfsc)?.toUpperCase() ?? null,
      });
      toast.success('Pay profile saved.');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(logError({ message: 'Could not save the pay profile', error: e, code: 'PAY_PROFILE_SAVE_FAILED' }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogShell open={employee !== null} title={`PAY PROFILE — ${employee?.name ?? ''}`} onClose={onClose} onSubmit={save} busy={busy} maxWidth='md'>
      {d && (
        <>
          <Typography variant='subtitle2'>Statutory</Typography>
          <Stack direction='row' flexWrap='wrap'>
            <FormControlLabel control={<Switch checked={d.pfApplicable} onChange={(e) => set({ pfApplicable: e.target.checked })} />} label='PF' />
            <FormControlLabel control={<Switch checked={d.esiApplicable} onChange={(e) => set({ esiApplicable: e.target.checked })} />} label='ESI' />
            <FormControlLabel control={<Switch checked={d.ptApplicable} onChange={(e) => set({ ptApplicable: e.target.checked })} />} label='Professional tax' />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            <TextField size='small' fullWidth label='TDS per month (₹)' type='number' value={d.tdsMonthly} onChange={(e) => set({ tdsMonthly: e.target.value })} />
            <TextField size='small' fullWidth label='UAN (PF)' value={d.uanNo ?? ''} onChange={(e) => set({ uanNo: e.target.value })} />
            <TextField size='small' fullWidth label='ESIC no.' value={d.esicNo ?? ''} onChange={(e) => set({ esicNo: e.target.value })} />
          </Stack>
          <Typography variant='subtitle2'>Payment</Typography>
          <TextField select size='small' label='Paid by' value={d.paymentMode} onChange={(e) => set({ paymentMode: e.target.value as PaymentMode })} sx={{ maxWidth: 220 }}>
            <MenuItem value='BANK'>Bank transfer</MenuItem>
            <MenuItem value='CHEQUE'>Cheque</MenuItem>
            <MenuItem value='CASH'>Cash</MenuItem>
          </TextField>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            <TextField size='small' fullWidth label='Account holder name' value={d.bankAccountName ?? ''} onChange={(e) => set({ bankAccountName: e.target.value })} />
            <TextField size='small' fullWidth label='Account number' value={d.bankAccountNo ?? ''} onChange={(e) => set({ bankAccountNo: e.target.value })} />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            <TextField size='small' fullWidth label='IFSC' value={d.bankIfsc ?? ''} onChange={(e) => set({ bankIfsc: e.target.value.toUpperCase() })} />
            <TextField size='small' fullWidth label='Bank' value={d.bankName ?? ''} onChange={(e) => set({ bankName: e.target.value })} />
            <TextField size='small' fullWidth label='Branch' value={d.bankBranch ?? ''} onChange={(e) => set({ bankBranch: e.target.value })} />
          </Stack>
        </>
      )}
    </DialogShell>
  );
}

function RevisionsDialog({ employee, canEdit, onClose, onChanged }: { employee: EmployeePay | null; canEdit: boolean; onClose: () => void; onChanged: () => void }) {
  const [rows, setRows] = useState<Revision[]>([]);
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState(localDate());
  const [gross, setGross] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!employee) return;
    setLoading(true);
    setupApi
      .revisions(employee.employeeId)
      .then(setRows)
      .catch((e) => toast.error(logError({ message: 'Could not load salary history', error: e, code: 'REVISIONS_FAILED' })))
      .finally(() => setLoading(false));
  }, [employee]);

  useEffect(() => {
    if (!employee) return;
    const d = new Date();
    setFrom(localDate(new Date(d.getFullYear(), d.getMonth(), 1)));
    setGross('');
    setNote('');
    load();
  }, [employee, load]);

  const add = async () => {
    if (!employee) return;
    if (!from || gross.trim() === '' || isBadNumber(gross) || Number(gross) < 0) {
      toast.error('Enter the date and a monthly gross amount.');
      return;
    }
    setBusy(true);
    try {
      await setupApi.addRevision(employee.employeeId, { effectiveFrom: from, monthlyGross: Number(gross), note: note.trim() || null });
      toast.success('Salary revision added.');
      setGross('');
      setNote('');
      load();
      onChanged();
    } catch (e) {
      toast.error(logError({ message: 'Could not add the revision', error: e, code: 'REVISION_ADD_FAILED' }));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r: Revision) => {
    if (!employee) return;
    setBusy(true);
    try {
      await setupApi.deleteRevision(employee.employeeId, r.id);
      toast.success('Revision deleted.');
      load();
      onChanged();
    } catch (e) {
      toast.error(logError({ message: 'Could not delete the revision', error: e, code: 'REVISION_DELETE_FAILED' }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalWrapper open={employee !== null} onClose={onClose} maxWidth='sm' sx={{ overflowY: 'auto' }}>
      <SectionTitle
        title={`SALARY — ${employee?.name ?? ''}`}
        action={
          <Button onClick={onClose} startIcon={<Icon icon='mdi:close' />}>
            Close
          </Button>
        }
      />
      <Typography variant='body2' color='text.secondary'>
        A month is paid on the revision in force on its last day. Earlier months keep the salary they had.
      </Typography>
      {loading && <LinearProgress />}
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell>From</TableCell>
            <TableCell align='right'>Monthly gross</TableCell>
            <TableCell>Note</TableCell>
            {canEdit && <TableCell />}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && !loading && (
            <TableRow>
              <TableCell {...({ colSpan: canEdit ? 4 : 3 } as object)}>
                <Typography variant='body2' color='text.secondary'>
                  No salary set.
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {rows.map((r) => (
            <TableRow key={r.id}>
              <TableCell>{fmtDate(r.effectiveFrom)}</TableCell>
              <TableCell align='right'>{inr(r.monthlyGross)}</TableCell>
              <TableCell>{r.note}</TableCell>
              {canEdit && (
                <TableCell align='right'>
                  <IconButton size='small' color='error' disabled={busy} onClick={() => remove(r)}>
                    <Icon icon='mdi:delete-outline' />
                  </IconButton>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {canEdit && (
        <Stack gap={1.5}>
          <Typography variant='subtitle2'>Add a revision</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            <TextField size='small' type='date' label='Effective from' value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField size='small' type='number' label='Monthly gross (₹)' value={gross} onChange={(e) => setGross(e.target.value)} />
          </Stack>
          <TextField size='small' label='Note' value={note} onChange={(e) => setNote(e.target.value)} placeholder='e.g. Annual increment' />
          <Button variant='contained' disabled={busy} onClick={add} sx={{ alignSelf: 'flex-end' }}>
            Add revision
          </Button>
        </Stack>
      )}
    </ModalWrapper>
  );
}

/** Each employee's current gross, statutory flags and bank details. */
export default function EmployeesTab({ canEdit }: { canEdit: boolean }) {
  const [rows, setRows] = useState<EmployeePay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profileFor, setProfileFor] = useState<EmployeePay | null>(null);
  const [salaryFor, setSalaryFor] = useState<EmployeePay | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setupApi
      .employees()
      .then(setRows)
      .catch((e) => setError(logError({ message: 'Could not load employees', error: e, code: 'PAY_EMPLOYEES_FAILED' })))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const flag = (on: boolean, label: string) => <Chip size='small' label={label} color={on ? 'primary' : 'default'} variant={on ? 'filled' : 'outlined'} />;

  return (
    <Stack gap={2}>
      {loading && <LinearProgress />}
      {error && <Alert severity='error'>{error}</Alert>}
      <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align='right'>Monthly gross</TableCell>
              <TableCell>Since</TableCell>
              <TableCell>Statutory</TableCell>
              <TableCell align='right'>TDS / month</TableCell>
              <TableCell>Payment</TableCell>
              <TableCell align='right'>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.employeeId} hover>
                <TableCell>
                  <Typography variant='body2' fontWeight={600}>
                    {r.name}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {[r.code, r.designation, r.department].filter(Boolean).join(' · ')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='body2'>{r.employmentStatus}</Typography>
                  <Typography variant='caption' color='text.secondary'>
                    Joined {fmtDate(r.joiningDate)}
                    {r.leavingDate ? ` · left ${fmtDate(r.leavingDate)}` : ''}
                  </Typography>
                </TableCell>
                <TableCell align='right' sx={{ fontWeight: 700, color: r.monthlyGross === null ? 'error.main' : undefined }}>
                  {r.monthlyGross === null ? 'Not set' : inr(r.monthlyGross)}
                </TableCell>
                <TableCell>{fmtDate(r.grossEffectiveFrom)}</TableCell>
                <TableCell>
                  <Stack direction='row' gap={0.5}>
                    {flag(r.profile.pfApplicable, 'PF')}
                    {flag(r.profile.esiApplicable, 'ESI')}
                    {flag(r.profile.ptApplicable, 'PT')}
                  </Stack>
                </TableCell>
                <TableCell align='right'>{r.profile.tdsMonthly ? inr(r.profile.tdsMonthly) : '—'}</TableCell>
                <TableCell>
                  <Typography variant='body2'>{r.profile.paymentMode}</Typography>
                  {bankComplete(r.profile) ? (
                    r.profile.paymentMode === 'BANK' && (
                      <Typography variant='caption' color='text.secondary'>
                        {r.profile.bankAccountNo} · {r.profile.bankIfsc}
                      </Typography>
                    )
                  ) : (
                    <Typography variant='caption' color='error'>
                      Bank details incomplete
                    </Typography>
                  )}
                </TableCell>
                <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title='Salary & revisions'>
                    <IconButton size='small' onClick={() => setSalaryFor(r)}>
                      <Icon icon='mdi:cash-edit' />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={canEdit ? 'Edit pay profile' : 'View pay profile'}>
                    <IconButton size='small' onClick={() => setProfileFor(r)}>
                      <Icon icon={canEdit ? 'mdi:pencil-outline' : 'mdi:eye-outline'} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {canEdit ? (
        <ProfileDialog employee={profileFor} onClose={() => setProfileFor(null)} onSaved={load} />
      ) : (
        <ModalWrapper open={profileFor !== null} onClose={() => setProfileFor(null)} maxWidth='sm'>
          <SectionTitle title={`PAY PROFILE — ${profileFor?.name ?? ''}`} />
          {profileFor && (
            <Stack gap={0.5}>
              {(
                [
                  ['PF', profileFor.profile.pfApplicable ? 'Yes' : 'No'],
                  ['ESI', profileFor.profile.esiApplicable ? 'Yes' : 'No'],
                  ['Professional tax', profileFor.profile.ptApplicable ? 'Yes' : 'No'],
                  ['TDS per month', inr(profileFor.profile.tdsMonthly)],
                  ['UAN', profileFor.profile.uanNo ?? '—'],
                  ['ESIC no.', profileFor.profile.esicNo ?? '—'],
                  ['Paid by', profileFor.profile.paymentMode],
                  ['Account holder', profileFor.profile.bankAccountName ?? '—'],
                  ['Account no.', profileFor.profile.bankAccountNo ?? '—'],
                  ['IFSC', profileFor.profile.bankIfsc ?? '—'],
                  ['Bank / branch', [profileFor.profile.bankName, profileFor.profile.bankBranch].filter(Boolean).join(', ') || '—'],
                ] as [string, string][]
              ).map(([k, v]) => (
                <Stack key={k} direction='row' justifyContent='space-between'>
                  <Typography variant='body2' color='text.secondary'>
                    {k}
                  </Typography>
                  <Typography variant='body2'>{v}</Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </ModalWrapper>
      )}
      <RevisionsDialog employee={salaryFor} canEdit={canEdit} onClose={() => setSalaryFor(null)} onChanged={load} />
    </Stack>
  );
}
